import { useRef } from 'react';

/**
 * Custom hook to manage overlay count in a React-friendly way.
 * Uses a shared counter across all instances to track open overlays.
 */

// Shared overlay counter using closure for better encapsulation
const createOverlayManager = (): {
  increment: () => number;
  decrement: () => number;
  getCount: () => number;
  reset: () => number;
} => {
  let count = 0;

  return {
    increment: () => ++count,
    decrement: () => (count = Math.max(0, count - 1)),
    getCount: () => count,
    reset: () => (count = 0),
  };
};

// Single instance shared across all overlays
const overlayManager = createOverlayManager();

export const useOverlayManager = (): {
  registerOverlay: () => void;
  unregisterOverlay: () => void;
  getOverlayCount: () => number;
  shouldRemoveBodyClass: () => boolean;
} => {
  const isRegistered = useRef(false);

  const registerOverlay = (): void => {
    if (!isRegistered.current) {
      overlayManager.increment();
      isRegistered.current = true;
    }
  };

  const unregisterOverlay = (): void => {
    if (isRegistered.current) {
      overlayManager.decrement();
      isRegistered.current = false;
    }
  };

  const getOverlayCount = (): number => overlayManager.getCount();

  const shouldRemoveBodyClass = (): boolean => overlayManager.getCount() === 0;

  return {
    registerOverlay,
    unregisterOverlay,
    getOverlayCount,
    shouldRemoveBodyClass,
  };
};

export default useOverlayManager;
